import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import PropTypes from 'prop-types';

import AcceptAgreementField from '../shared/AcceptAgreementField';
import Button from '../../../../shared/_TBComponents/Button';
import CommonFields from '../shared/CommonFields';
import GoogleAuthSection from '../shared/GoogleAuthSection';
import RegistrationFormWrapper from '../shared/RegistrationFormWrapper';
import {
  RegistrationSchema,
  RegistrationSchemaGoogle,
  RegistrationSchemaSsoUsername,
  RegistrationSchemaUsername,
} from '../../../../shared/utils/registrationSchemaHelper';
import {
  cancelRegistration,
  registerUser,
} from '../../ducks/registrationActions';
import SSOSection from '../shared/SSOSection';

const FormGroup = styled('div')`
  z-index: 1;
  
  label {
    width: 100%;
  }

  @media screen and (max-width: 425px) {
    margin-bottom: 10px;
  }
`;

const ExistingAccountRegistrationForm = ({ tokenData }) => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const googleUser = useSelector((state) => state.registration.data.googleUser);
  const isRegisteringWithGoogle = useSelector(
    (state) => state.registration.data.isRegisteringWithGoogle,
  );
  const isRegisteringWithSSO = useSelector((state) => state.auth.data.isRegisteringWithSSO);
  const isSubmitting = useSelector((state) => state.registration.ui.isSubmitting);

  const dispatch = useDispatch();
  const theme = useTheme();

  const [googleResponse, setGoogleResponse] = useState(null);

  const isCoach = !!currentUser.admin || !!tokenData?.isCoach;

  /*
   * The token flow is for users invited by email. An athlete with no email never
   * receives one, so a token always means this is the email form — and reading
   * the username off currentUser there would pair the token's userId with the
   * logged-in user's username.
   */
  const registrantUsername = tokenData ? '' : (currentUser.username || '').trim();

  /*
   * Athletes on a usernames-enabled account identify by the username their coach
   * assigned them, not an email. Coaches always keep the email field so they can
   * still log in and reset their password by email. Falls back to email when the
   * athlete has no username (accounts that enabled the setting after their
   * athletes were already created with emails).
   */
  const registersWithUsername = !!currentUser.organizationUsernamesEnabled
    && !isCoach
    && !!registrantUsername;

  /*
   * Google identifies a user by an email these athletes do not have, so its
   * button is hidden for them below. Hiding it is not enough on its own: the
   * flag lives in Redux, and the disconnect control that clears it lives inside
   * the very section being hidden, so the form must not depend on that render
   * condition to stay correct. Google mode is neutralised here instead, and
   * every branch below reads this rather than the raw flag.
   */
  const isRegisteringWithGoogleForForm = isRegisteringWithGoogle && !registersWithUsername;

  const getInitialValues = () => {
    let accountCode = '';
    let countryCode = '';
    let emailAddress = '';
    let firstName = '';
    let gender = '';
    let lastName = '';
    let phone = '';
    let userId = '';

    if (isRegisteringWithGoogleForForm) {
      accountCode = currentUser.accountCode || '';
      emailAddress = googleUser.email || '';
      firstName = googleUser.givenName || '';
      lastName = googleUser.familyName || '';
      userId = currentUser.id || '';
    } else if (tokenData) {
      accountCode = tokenData.accountCode || '';
      countryCode = tokenData.countryCode || '';
      emailAddress = tokenData.email || '';
      firstName = tokenData.firstName || '';
      gender = tokenData.gender || '';
      lastName = tokenData.lastName || '';
      phone = tokenData.phone || '';
      userId = tokenData.userId || '';
    } else {
      accountCode = currentUser.accountCode || '';
      countryCode = currentUser.phoneCountryCode || '';
      emailAddress = currentUser.email || '';
      firstName = currentUser.first || '';
      gender = currentUser.gender || '';
      lastName = currentUser.last || '';
      phone = currentUser.phone || '';
      userId = currentUser.id || '';
    }

    return {
      acceptAgreement: false,
      accountCode,
      countryCode,
      emailAddress,
      firstName,
      gender,
      lastName,
      password: '',
      phone,
      userId,
      username: registrantUsername,
    };
  };

  const getValidationSchema = () => {
    if (isRegisteringWithGoogleForForm) {
      return RegistrationSchemaGoogle;
    }
    if (isRegisteringWithSSO) {
      return registersWithUsername ? RegistrationSchemaSsoUsername : RegistrationSchemaGoogle;
    }
    return registersWithUsername ? RegistrationSchemaUsername : RegistrationSchema;
  };

  const handleCancel = () => {
    dispatch(cancelRegistration(!!currentUser.id));
  };

  const handleGoogleConnect = (response) => {
    setGoogleResponse(response);
  };

  const handleRegistration = (values) => {
    let submissionObject = {};

    if (isRegisteringWithGoogleForForm) {
      submissionObject = {
        acceptAgreement: true,
        accountCode: values.accountCode,
        authenticationMethod: 'GOOGLE',
        client_id: process.env.CLIENT_ID,
        client_secret: process.env.CLIENT_SECRET,
        countryCode: values.countryCode,
        emailAddress: values.emailAddress,
        firstName: values.firstName,
        gender: values.gender,
        googleToken: googleResponse.tokenId,
        lastName: values.lastName,
        mobileNumber: values.phone,
        registrationType: 'COMPLETE_REGISTRATION',
        userId: values.userId,
      };
    } else {
      submissionObject = {
        acceptAgreement: values.acceptAgreement,
        accountCode: values.accountCode,
        authenticationMethod: isRegisteringWithSSO ? 'SSO' : 'PASSWORD',
        client_id: process.env.CLIENT_ID,
        client_secret: process.env.CLIENT_SECRET,
        countryCode: values.countryCode,
        ...(registersWithUsername
          ? { username: values.username }
          : { emailAddress: values.emailAddress }),
        firstName: values.firstName,
        gender: values.gender,
        lastName: values.lastName,
        organizationSsoId: isRegisteringWithSSO ? currentUser.organizationSsoId : undefined,
        password: values.password,
        mobileNumber: values.phone,
        registrationType: 'COMPLETE_REGISTRATION',
        userId: values.userId,
      };
    }

    dispatch(registerUser(submissionObject));
  };

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues()}
      onSubmit={(values) => {
        handleRegistration(values);
      }}
      validationSchema={getValidationSchema()}
      render={({
        errors,
        handleSubmit,
        setFieldValue,
        touched,
        values,
      }) => (
        <RegistrationFormWrapper
          subtitle='Enter the necessary details below to complete your registration.'
          title='Registration'
        >
          <Form
            id='registration'
            name='registration'
          >
            {!isRegisteringWithSSO && !registersWithUsername ? (
              <GoogleAuthSection
                onGoogleConnect={handleGoogleConnect}
              />
            ) : null}
            {isRegisteringWithSSO ? <SSOSection /> : null}
            <CommonFields
              errors={errors}
              setFieldValue={setFieldValue}
              showGenderField={!isCoach}
              showPasswordFields={!isRegisteringWithGoogleForForm && !isRegisteringWithSSO}
              showReadOnlyUsername={registersWithUsername}
              touched={touched}
              usernameAlias={currentUser.organizationUsernameAlias}
              values={values}
            />
            <AcceptAgreementField
              error={errors.acceptAgreement}
              setFieldValue={setFieldValue}
              showAgeNotice={!isCoach}
              touched={touched.acceptAgreement}
              values={{
                acceptAgreement: values.acceptAgreement,
              }}
            />
            <FormGroup>
              <Button
                bgColor={theme.colors.orange}
                disabled={
                  errors.password
                    || (!registersWithUsername && errors.emailAddress)
                    || errors.confirmedPassword
                    || errors.acceptAgreement
                    || errors.phone
                    || errors.countryCode
                    || isSubmitting
                    || !values.acceptAgreement
                    || (registersWithUsername
                      ? !values.username?.trim()
                      : !values.emailAddress?.trim())
                    || !values.firstName?.trim()
                    || !values.lastName?.trim()
                    || (
                      !isRegisteringWithGoogleForForm
                      && !isRegisteringWithSSO
                      && (!values.password?.trim() || !values.confirmedPassword?.trim())
                    )
                }
                fontSize='14px'
                fullWidth
                id='submit'
                large
                name='submit'
                noBorder
                onClick={handleSubmit}
                rounded
                txtColor={theme.colors.white}
                type='submit'
                upperCase
              />
              <Button
                fontSize='14px'
                fullWidth
                large
                name='Return to login'
                noBorder
                onClick={handleCancel}
                styledWrapperProps={{ marginTop: '15px' }}
                txtColor={theme.colors.lightText[1]}
                type='button'
                upperCase
              />
            </FormGroup>
          </Form>
        </RegistrationFormWrapper>
      )}
    />
  );
};

ExistingAccountRegistrationForm.propTypes = {
  tokenData: PropTypes.shape({
    accountCode: PropTypes.number,
    countryCode: PropTypes.string,
    email: PropTypes.string,
    firstName: PropTypes.string,
    gender: PropTypes.number,
    isCoach: PropTypes.bool,
    lastName: PropTypes.string,
    phone: PropTypes.string,
    userId: PropTypes.number,
  }),
};

ExistingAccountRegistrationForm.defaultProps = {
  tokenData: null,
};

export default ExistingAccountRegistrationForm;
